import { HttpClient } from '../http-client';
import type { BaseResponse } from '../types/common';
import type {
  UserImportRequest,
  UserResponse,
  UserSearchRequest,
  UserSearchResponse,
  UserSetProjectRequest,
  UserSetTenantRequest,
  UserSetTenantsRequest,
  UserTenantRequest,
  UserUpdateRequest,
  UserUpdateRolesRequest
} from '../types/user.types';

function normalizePageable(params: UserSearchRequest): UserSearchRequest {
  return {
    ...params,
    pageable: {
      pageNumber: params.pageable?.pageNumber ?? 0,
      pageSize: params.pageable?.pageSize ?? 20,
      sort: params.pageable?.sort ?? { orders: [] }
    }
  };
}

class UserService {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  async getById(userId: string): Promise<UserResponse> {
    return this.http.requestAndValidate<UserResponse>('/users/get-user-by-id', {
      method: 'POST',
      body: { userId }
    });
  }

  async getByEmail(email: string): Promise<UserResponse> {
    return this.http.requestAndValidate<UserResponse>('/users/get-user-by-email', {
      method: 'POST',
      body: { email }
    });
  }

  async search(params: UserSearchRequest = {}): Promise<UserSearchResponse> {
    return this.http.requestAndValidate<UserSearchResponse>('/users/search-users', {
      method: 'POST',
      body: normalizePageable(params)
    });
  }

  async update(data: UserUpdateRequest): Promise<UserResponse> {
    return this.http.requestAndValidate<UserResponse>('/users/update-user', {
      method: 'POST',
      body: data
    });
  }

  async updateRoles(data: UserUpdateRolesRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/update-user-roles', {
      method: 'POST',
      body: data
    });
  }

  async delete(userId: string): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/delete-user', {
      method: 'POST',
      body: { userId }
    });
  }

  async enable(userId: string): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/enable-user', {
      method: 'POST',
      body: { userId }
    });
  }

  async disable(userId: string): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/disable-user', {
      method: 'POST',
      body: { userId }
    });
  }

  async importUser(data: UserImportRequest): Promise<UserResponse> {
    return this.http.requestAndValidate<UserResponse>('/users/import-user', {
      method: 'POST',
      body: data
    });
  }

  async addToTenant(data: UserTenantRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/add-user-to-tenant', {
      method: 'POST',
      body: data
    });
  }

  async removeFromTenant(data: UserTenantRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/remove-user-from-tenant', {
      method: 'POST',
      body: data
    });
  }

  async setTenants(data: UserSetTenantsRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/set-user-tenants', {
      method: 'POST',
      body: data
    });
  }

  async setTenant(data: UserSetTenantRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/set-tenant', {
      method: 'POST',
      body: data
    });
  }

  async setProject(data: UserSetProjectRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/users/set-project', {
      method: 'POST',
      body: data
    });
  }
}

export { UserService };
